import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CasinoHome,
  CasinoHomeSection,
  CasinoHomeSectionHeader,
  CasinoHomeSectionHeaderLeft,
  CasinoHomeSectionHeaderRight,
  CasinoHomeSectionSlider,
  CasinoHomeSectionSliderItem,
} from "./styled";
import {
  MobileHomeContainer,
  MobileHomeSectionContainer,
  HomeSectionHeader,
  HomeSectionHeaderTitle,
  HomeSectionHeaderContent,
  HomeSectionGameList,
  HomeSectionGames,
} from "../Landing/styled";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import { UserContext } from "../../Providers/UserContext";

import ogsGameList from "../../config/gameList.json";
import newGameList from "../../config/newGame.json";

// Skeletons
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

type Category = {
  name: string;
  path?: string;
};

type RgsGame = {
  gameCode: string;
  name: string;
  softwareProvider: string;
  uuid: string;
};

type OgsGame = {
  provider: string;
  gameId?: string;
  gameName?: string;
  displayName?: string;
  iconUrl: string;
};

type NewGame = {
  gameCode: string;
  gameName: string;
};

interface Props {
  categories: Category[];
  category?: Category;
  setCategory: (category: Category) => void;
  /** Control loading explicitly. When true, skeletons show. */
  isLoading?: boolean;
}

const OGS_PROVIDERS = new Set([
  "NETENT2",
  "AMATIC2",
  "NOVOMATIC2",
  "ARISTOCRAT2",
  "EGT2",
  "Merkur",
  "PlayNGo",
  "PragmaticPlay",
  "River",
  "Scientific",
  "Yggdrasil",
  "Zeppelin",
]);

const DESKTOP_SKELETON_HEIGHT = 160;
const MOBILE_SKELETON_HEIGHT = 110;
const imgRound16: React.CSSProperties = { borderRadius: 16 };
const imgRound10: React.CSSProperties = { borderRadius: 10 };
const SKELETON_COUNT = 12;

const CategoryList: React.FC<Props> = ({ categories, setCategory, isLoading = false }) => {
  const navigate = useNavigate();

  const {
    netentGame,
    amaticGame,
    novomaticGame,
    quickspinGame,
    egtGame,
    ainsworthGame,
    egteqGame,
    egtdigitalGame,
    pragmaticGame,
    pateGame,
    merkurGame,
  } = useContext(UserContext);

  /** IMPORTANT: Only the parent controls loading. No "length===0" fallback to avoid skeleton flicker/loop. */
  const showSkeletons = !!isLoading;

  // Map RGS provider -> array from context
  const rgsProviderMap = useMemo<Record<string, RgsGame[]>>(
    () => ({
      NETENT: netentGame,
      AMATIC: amaticGame,
      NOVOMATIC: novomaticGame,
      AINSWORTH: ainsworthGame,
      QUICKSPIN: quickspinGame,
      EGT: egtGame,
      EGT_EQ: egteqGame,
      EGT_DIGITAL: egtdigitalGame,
      PRAGMATIC: pragmaticGame,
      PATE: pateGame,
      MERKUR: merkurGame,
    }),
    [
      netentGame,
      amaticGame,
      novomaticGame,
      ainsworthGame,
      quickspinGame,
      egtGame,
      egteqGame,
      egtdigitalGame,
      pragmaticGame,
      pateGame,
      merkurGame,
    ]
  );

  // OGS provider -> games
  const ogsByProvider = useMemo(() => {
    const map = new Map<string, OgsGame[]>();
    (ogsGameList as OgsGame[]).forEach((g) => {
      if (!map.has(g.provider)) map.set(g.provider, []);
      map.get(g.provider)!.push(g);
    });
    return map;
  }, []);

  const getCountForCategory = (cat: Category): number => {
    if (cat.name === "New Games") return (newGameList as NewGame[]).length;
    if (OGS_PROVIDERS.has(cat.name)) return ogsByProvider.get(cat.name)?.length ?? 0;
    return rgsProviderMap[cat.name]?.length ?? 0;
  };

  const handleGamePlay = (games: any, provider: string, api?: "news" | "ogs") => {
    if (api === "news") {
      const g = games as NewGame;
      navigate(`/casino/play/${g.gameCode}`, { state: { gameItem: g, provider, api } });
      return;
    }
    if (api === "ogs") {
      const g = games as OgsGame;
      if (provider === "EGT2" || provider === "ARISTOCRAT2") {
        navigate(`/casino/play/${g.gameId}`, { state: { gameItem: g, provider, api } });
      } else {
        navigate(`/casino/play/${g.gameName}`, { state: { gameItem: g, provider, api } });
      }
      return;
    }
    const g = games as RgsGame;
    navigate(`/casino/play/${g.gameCode}`, { state: { gameItem: g } });
  };

  const buildItemsForCategory = (cat: Category, limit = 12) => {
    if (cat.name === "New Games") {
      return (newGameList as NewGame[]).slice(0, limit).map((g, idx) => ({
        key: `${g.gameCode}-${idx}`,
        title: g.gameName,
        imgSrc: `/game-icon/${g.gameCode}.png`,
        onClick: () => handleGamePlay(g, cat.name, "news"),
      }));
    }

    if (OGS_PROVIDERS.has(cat.name)) {
      return (ogsByProvider.get(cat.name) ?? []).slice(0, limit).map((g, idx) => ({
        key: `${g.gameId ?? g.gameName ?? g.displayName ?? idx}-${idx}`,
        title: g.displayName ?? g.gameName ?? cat.name,
        imgSrc: g.iconUrl,
        onClick: () => handleGamePlay(g, cat.name, "ogs"),
      }));
    }

    const list = rgsProviderMap[cat.name] ?? [];
    return list.slice(0, limit).map((g, idx) => ({
      key: `${g.gameCode}-${idx}`,
      title: g.name,
      imgSrc: `https://back-office.b-cdn.net/icons/${g?.softwareProvider}/${g?.gameCode}.png`,
      onClick: () => handleGamePlay(g, cat.name),
    }));
  };

  const skeletonArray = useMemo(() => Array.from({ length: SKELETON_COUNT }), []);

  return (
    <>
      {/* Desktop / Tablet */}
      <CasinoHome>
        {(showSkeletons ? [0, 1, 2, 3] : categories).map((catRaw: any, sectionIdx: number) => {
          const cat = showSkeletons ? ({ name: "", path: "" } as Category) : (catRaw as Category);
          const items = showSkeletons ? skeletonArray : buildItemsForCategory(cat, 12);
          const count = showSkeletons ? undefined : getCountForCategory(cat);

          return (
            <CasinoHomeSection key={showSkeletons ? `skeleton-sec-${sectionIdx}` : (cat.path ?? cat.name)}>
              <CasinoHomeSectionHeader>
                <CasinoHomeSectionHeaderLeft>
                  <span>{showSkeletons ? <Skeleton width={120} height={20} baseColor="gray" /> : cat.name}</span>
                  <label>{showSkeletons ? <Skeleton width={40} height={18} baseColor="gray" /> : count}</label>
                </CasinoHomeSectionHeaderLeft>
                <CasinoHomeSectionHeaderRight>
                  {showSkeletons ? (
                    <Skeleton width={40} height={18} />
                  ) : (
                    <span onClick={() => setCategory(cat)}>All</span>
                  )}
                </CasinoHomeSectionHeaderRight>
              </CasinoHomeSectionHeader>

              <CasinoHomeSectionSlider>
                <Swiper
                  slidesPerView={4}
                  spaceBetween={15}
                  className="mySwiper"
                  freeMode
                  modules={[FreeMode]}
                  breakpoints={{
                    1630: { slidesPerView: 7, spaceBetween: 15 },
                    1440: { slidesPerView: 6, spaceBetween: 15 },
                    1250: { slidesPerView: 5, spaceBetween: 15 },
                  }}
                >
                  {(showSkeletons ? skeletonArray : items).map((g: any, idx: number) => (
                    <SwiperSlide
                      key={showSkeletons ? `skeleton-slide-${idx}` : g.key}
                      onClick={showSkeletons ? undefined : g.onClick}
                    >
                      <CasinoHomeSectionSliderItem>
                        {showSkeletons ? (
                          <Skeleton height={DESKTOP_SKELETON_HEIGHT} style={imgRound16} baseColor="gray" />
                        ) : (
                          <img
                            src={g.imgSrc}
                            alt={g.title}
                            width="100%"
                            height="100%"
                            style={imgRound16}
                            loading="lazy"
                          />
                        )}
                      </CasinoHomeSectionSliderItem>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </CasinoHomeSectionSlider>
            </CasinoHomeSection>
          );
        })}
      </CasinoHome>

      {/* Mobile */}
      <MobileHomeContainer>
        {(showSkeletons ? [0, 1, 2, 3] : categories).map((catRaw: any, sectionIdx: number) => {
          const cat = showSkeletons ? ({ name: "", path: "" } as Category) : (catRaw as Category);
          const items = showSkeletons ? skeletonArray : buildItemsForCategory(cat, 12);
          const count = showSkeletons ? undefined : getCountForCategory(cat);

          return (
            <MobileHomeSectionContainer key={showSkeletons ? `mob-skeleton-${sectionIdx}` : (cat.path ?? cat.name)}>
              <HomeSectionHeader>
                <HomeSectionHeaderTitle>
                  {showSkeletons ? <Skeleton width={120} height={22} /> : cat.name}
                </HomeSectionHeaderTitle>
                <HomeSectionHeaderContent>
                  {showSkeletons ? (
                    <>
                      <Skeleton width={30} height={18} baseColor="gray" />
                      <Skeleton width={60} height={18} style={{ marginLeft: 8 }} baseColor="gray" />
                    </>
                  ) : (
                    <>
                      <label onClick={() => setCategory(cat)}>All</label>
                      <span>{count} games</span>
                    </>
                  )}
                </HomeSectionHeaderContent>
              </HomeSectionHeader>

              <HomeSectionGameList>
                {(showSkeletons ? skeletonArray : items).map((g: any, idx: number) =>
                  showSkeletons ? (
                    <HomeSectionGames key={`mob-skel-${idx}`}>
                      <div>
                        <Skeleton height={MOBILE_SKELETON_HEIGHT} style={imgRound10} baseColor="gray" />
                      </div>
                      <span>
                        <Skeleton width={100} height={16} baseColor="gray" />
                      </span>
                    </HomeSectionGames>
                  ) : (
                    <HomeSectionGames key={g.key} onClick={g.onClick}>
                      <div>
                        <img
                          src={g.imgSrc}
                          alt={g.title}
                          width="100%"
                          height="100%"
                          style={imgRound10}
                          loading="lazy"
                        />
                      </div>
                      <span>{g.title}</span>
                    </HomeSectionGames>
                  )
                )}
              </HomeSectionGameList>
            </MobileHomeSectionContainer>
          );
        })}
      </MobileHomeContainer>
    </>
  );
};

export default CategoryList;
